import React, { useState, useRef, useEffect } from 'react'
import { FaSearch, FaTimes } from 'react-icons/fa'
import './CustomSearch.scss'

const CustomSearch = ({
  items = [],
  onSelect,
  placeholder = "Search",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [filteredResults, setFilteredResults] = useState([])
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [dropdownPosition, setDropdownPosition] = useState('left')
  const [dropdownWidth, setDropdownWidth] = useState(250) // Default to search input width
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setHighlightedIndex(-1)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      calculateDropdownDimensions()
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, filteredResults])

  const calculateDropdownDimensions = () => {
    if (!inputRef.current || filteredResults.length === 0) return

    const inputRect = inputRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const inputWidth = inputRect.width

    // Calculate required width based on content
    let maxContentWidth = inputWidth // Start with input width as minimum

    filteredResults.forEach(patient => {
      // Estimate text width (rough calculation)
      const idLength = (patient.custom_id || '').length
      const emailLength = (patient.email || '').length

      // Rough character width estimation: 8px per character + padding
      const estimatedIdWidth = idLength * 8 + 32 // padding
      const estimatedEmailWidth = emailLength * 7 + 32 // email is smaller font

      const contentWidth = Math.max(estimatedIdWidth, estimatedEmailWidth)
      maxContentWidth = Math.max(maxContentWidth, contentWidth)
    })

    // Cap maximum width to prevent extremely wide dropdowns
    const maxAllowedWidth = Math.min(maxContentWidth, 450)
    setDropdownWidth(maxAllowedWidth)

    // Position dropdown based on available space
    const spaceOnRight = viewportWidth - inputRect.left
    const spaceOnLeft = inputRect.right

    if (maxAllowedWidth <= spaceOnRight) {
      // Enough space on the right, align to left edge of input
      setDropdownPosition('left')
    } else if (maxAllowedWidth <= spaceOnLeft) {
      // Not enough space on right, but enough on left, align to right edge of input
      setDropdownPosition('right')
    } else {
      // Not enough space on either side, align to right edge of input (safer in header)
      setDropdownPosition('right')
    }
  }

  const filterResults = (query) => {
    if (!query.trim()) {
      return []
    }

    const lowercaseQuery = query.toLowerCase()
    return items.filter(item => {
      const customId = (item.custom_id || '').toLowerCase()
      const email = (item.email || '').toLowerCase()

      return customId.includes(lowercaseQuery) || email.includes(lowercaseQuery)
    }).slice(0, 8) // Limit to 8 results for performance
  }

  const highlightMatch = (text, query) => {
    if (!query || !text) return text

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    return text.replace(regex, '<mark>$1</mark>')
  }

  const handleInputChange = (e) => {
    const value = e.target.value
    setSearchValue(value)

    const results = filterResults(value)
    setFilteredResults(results)
    setIsOpen(results.length > 0)
    setHighlightedIndex(-1)
  }

  const handleInputFocus = () => {
    if (searchValue.trim()) {
      const results = filterResults(searchValue)
      setFilteredResults(results)
      setIsOpen(results.length > 0)
    }
  }

  const handleKeyDown = (e) => {
    if (!isOpen || filteredResults.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev =>
          prev < filteredResults.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredResults.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0) {
          handleSelectItem(filteredResults[highlightedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setHighlightedIndex(-1)
        inputRef.current?.blur()
        break
    }
  }

  const handleSelectItem = (item) => {
    onSelect(item)
    setSearchValue("")
    setFilteredResults([])
    setIsOpen(false)
    setHighlightedIndex(-1)
    inputRef.current?.blur()
  }

  const handleClear = () => {
    setSearchValue("")
    setFilteredResults([])
    setIsOpen(false)
    setHighlightedIndex(-1)
    inputRef.current?.focus()
  }

  const renderResult = (patient, index) => {
    const isHighlighted = index === highlightedIndex

    return (
      <div
        key={patient.custom_id}
        className={`search-result ${isHighlighted ? 'highlighted' : ''}`}
        onClick={() => handleSelectItem(patient)}
        onMouseEnter={() => setHighlightedIndex(index)}
      >
        <div className="patient-info">
          <div
            className="custom-patient-id"
            dangerouslySetInnerHTML={{
              __html: highlightMatch(patient.custom_id, searchValue)
            }}
          />
          <div
            className="patient-email"
            dangerouslySetInnerHTML={{
              __html: highlightMatch(patient.email, searchValue)
            }}
          />
          <div className="patient-study">
            {patient.shoulder_arthroplasty && <span className="study-badge">SAp</span>}
            {patient.shoulder_arthroscopy && <span className="study-badge">SAs</span>}
            {patient.elbow && <span className="study-badge">E</span>}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`custom-search ${className}`} ref={dropdownRef}>
      <div className="search-input-container" ref={inputRef}>
        <div className="search-input-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            value={searchValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="search-input"
            autoComplete="off"
          />
          {searchValue && (
            <FaTimes
              className="clear-icon"
              onClick={handleClear}
            />
          )}
        </div>
      </div>

      {isOpen && filteredResults.length > 0 && (
        <div
          className={`search-dropdown ${dropdownPosition === 'right' ? 'align-right' : dropdownPosition === 'center' ? 'align-center' : ''}`}
          style={{ width: `${dropdownWidth}px` }}
        >
          <div className="search-results">
            {filteredResults.map((patient, index) => renderResult(patient, index))}
          </div>
          {filteredResults.length === 8 && (
            <div className="search-footer">
              Showing first 8 results. Refine your search for more specific results.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default CustomSearch